import { CaretRightIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Flex,
  Grid,
  Heading,
  IconButton,
  Text,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import { FilledCircleIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import {
  CommonTextInput,
  DEFAULT_TEXT_DEBOUNCE_MS,
} from 'components/common/form/text';
import { CommonTrainingStatusIcon } from 'components/common/pitch-lists/training-status-icon';
import { PitchPriorityBadge } from 'components/common/pitch-priority-badge';
import { PitcherHandBadge } from 'components/common/pitcher-hand-badge';
import { CommonTable } from 'components/common/table';
import { CommonTooltip } from 'components/common/tooltip';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import { CheckedContext } from 'contexts/layout/checked.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { PITCH_TYPE_FILTER_OPTIONS } from 'enums/filters';
import { TABLES } from 'enums/tables';
import { t } from 'i18next';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSortable } from 'interfaces/tables/sorting';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import {
  MAX_BREAKS_DELTA_IN,
  MAX_BREAKS_DELTA_IN_TRAJEKT_36,
  MAX_PLATE_DELTA_IN,
  MAX_SPIN_DELTA_RPM,
  MAX_VELO_DELTA_MPH,
} from 'lib_ts/enums/pitch-performance';
import { PitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IGroupedFires } from 'lib_ts/interfaces/data/i-pitch-performance';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { first } from 'lodash';
import { useContext, useMemo } from 'react';

const COMPONENT_NAME = 'PitchPerfGroupTable';

const ONLY_SHOW_CURRENT_MS = true;
const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

interface IProps {
  machine: IMachine;
  onDrillDown: (id: string) => void;
}

export const PitchPerfGroupTable = ({ onDrillDown, machine }: IProps) => {
  const aggDict = usePitchPerformanceStore((s) => s.aggDict);
  const loading = usePitchPerformanceStore((s) => s.loading);
  const filterBallType = usePitchPerformanceStore((s) => s.filters.ball_type);
  const groupFilters = usePitchPerformanceStore((s) => s.groupFilters);
  const groups = usePitchPerformanceStore((s) => s.groups);

  const { checkedIDs } = useContext(CheckedContext);

  // table toolbar can further filter what's shown w/o affecting what's plotted + shown in metrics
  const tableGroups = useMemo(() => {
    const { search, pitchType, onlyShowChecked } = groupFilters;
    const safeSearch = search.trim().toLowerCase();

    const output = groups
      .filter(
        // groups table only shows current MS groups
        (g) => !ONLY_SHOW_CURRENT_MS || g.isCurrentMs
      )
      .filter(
        // filter based on user's search string
        (g) => !safeSearch || g.pitch.name.toLowerCase().includes(safeSearch)
      )
      .filter(
        // filter based on user's pitch type
        (g) => !pitchType || g.pitch.type === pitchType
      )
      .filter(
        // users can check/uncheck groups here to control what's plotted, optionally hide un-checked groups
        (g) => !onlyShowChecked || checkedIDs.includes(g._id)
      );

    return output;
  }, [groups, groupFilters, checkedIDs]);

  const pagination: ITablePageable = {
    paginationID: PaginationID.PitchPerformanceGroupList,
    total: tableGroups.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'lastFired',
      dir: 1,
    },
  };

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    // just a placeholder
    checkedActions: () => [],
  };

  const MAX_BREAKS_DELTA_INCHES =
    filterBallType === BallType.Trajekt_36
      ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
      : MAX_BREAKS_DELTA_IN;

  // MARK: columns
  const columns = useMemo<ITypedTableColumn<IGroupedFires>[]>(
    () => [
      {
        label: 'common.pitch',
        key: 'lastFired',
        formatFn: (m) => {
          const pitchName = `${m.pitch.name} ${m.pitch.type}`;
          return (
            <Flex gap="2" align="start">
              <Text style={{ color: `rgb(${m.rgb})` }}>
                <FilledCircleIcon />
              </Text>
              <Flex direction="column" gap="1">
                <Flex gap="1">
                  <CommonTooltip
                    text_md={pitchName}
                    trigger={
                      <Text
                        onClick={() => {
                          navigator.clipboard.writeText(m.pitch._id);

                          NotifyHelper.success({
                            message_md: t('common.x-copied-to-clipboard', {
                              x: 'Pitch ID',
                            }),
                          });
                        }}
                        className="cursor-pointer"
                      >
                        {pitchName}
                      </Text>
                    }
                  />
                </Flex>

                {m.lastFired && (
                  <Text size="1" color={RADIX.COLOR.NEUTRAL}>
                    {format(parseISO(m.lastFired), 'MM/dd/yyyy HH:mm z', {
                      timeZone: LOCAL_TIMEZONE,
                    })}
                  </Text>
                )}

                <Flex gap="1">
                  <PitcherHandBadge
                    value={BallHelper.getPitcherHand(m.target.traj.px)}
                  />

                  <PitchPriorityBadge value={m.pitch.priority} />

                  {!ONLY_SHOW_CURRENT_MS && m.isCurrentMs && (
                    <Badge color={RADIX.COLOR.SUCCESS}>Current MS</Badge>
                  )}
                </Flex>
              </Flex>
            </Flex>
          );
        },
      },
      {
        label: 'common.shots',
        key: '_shots',
        align: 'center',
        sortRowsFn: (a, b, dir) =>
          (a.fires.length > b.fires.length ? 1 : -1) * dir,
        formatFn: (m) => (
          <Flex gap="1" align="center" justify="center">
            <Text>{m.fires.length}</Text>
            {m.fires.length > 0 && (
              <IconButton
                size="1"
                className="btn-floating"
                variant={RADIX.BUTTON.VARIANT.BORDERLESS}
                color={RADIX.COLOR.NEUTRAL}
                onClick={() => onDrillDown(m._id)}
              >
                <CaretRightIcon />
              </IconButton>
            )}
          </Flex>
        ),
      },
      {
        label: 'Status',
        key: '_status',
        align: 'center',
        tooltipFn: (m) => {
          return PitchListHelper.getTrainedStatusTooltip({
            tms: m.isCurrentMs ? m.pitch._mx_tms : undefined,
            last_hardware_changed: machine.last_hardware_changed,
          });
        },
        sortRowsFn: (a, b, dir: number) => {
          const [aValue, bValue] = [a, b].map((m) => {
            const entry = aggDict[m.target.ms.matching_hash ?? ''];

            if (!entry) {
              // untrained
              return 0;
            }

            if (entry?.trained && m.target.ms.precision_trained) {
              // precision
              return 2;
            }

            if (entry.trained) {
              // trained
              return 1;
            }

            return 0;
          });

          return dir * (aValue > bValue ? -1 : 1);
        },
        formatFn: (m) => (
          <Box mt="1">
            <CommonTrainingStatusIcon
              tms={m.isCurrentMs ? m.pitch._mx_tms : undefined}
              last_hardware_changed={machine.last_hardware_changed}
              autoRefresh={false}
              canRefresh={false}
            />
          </Box>
        ),
      },
      {
        label: 'common.speed',
        subLabel: 'mph',
        key: '_speed',
        sortRowsFn: (a, b, dir) =>
          (a.average.speedMPH > b.average.speedMPH ? 1 : -1) * dir,
        formatFn: (m) => (
          <Flex direction="column" pt="2" pb="2">
            <Box>
              <Text>{m.average.speedMPH.toFixed(1)}</Text>
            </Box>
            <Box>
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                Target: {m.target.speedMPH.toFixed(1)}
              </Text>
            </Box>
            <Box>
              <Badge
                color={
                  Math.abs(m.delta.speedMPH) > MAX_VELO_DELTA_MPH
                    ? RADIX.COLOR.DANGER
                    : RADIX.COLOR.SUCCESS
                }
              >
                {m.delta.speedMPH > 0 && '+'}
                {m.delta.speedMPH.toFixed(1)}
              </Badge>
            </Box>
          </Flex>
        ),
      },
      {
        label: 'common.spin',
        subLabel: 'rpm',
        key: '_netSpin',
        sortRowsFn: (a, b, dir) =>
          (a.average.netSpin > b.average.netSpin ? 1 : -1) * dir,
        formatFn: (m) => (
          <Flex direction="column" pt="2" pb="2">
            <Box>
              <Text>{m.average.netSpin.toFixed(0)}</Text>
            </Box>

            <Box>
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                Target: {m.target.netSpin.toFixed(0)}
              </Text>
            </Box>

            <Box>
              <Badge
                color={
                  Math.abs(m.delta.netSpin) > MAX_SPIN_DELTA_RPM
                    ? RADIX.COLOR.DANGER
                    : RADIX.COLOR.SUCCESS
                }
              >
                {m.delta.netSpin > 0 && '+'}
                {m.delta.netSpin.toFixed(0)}
              </Badge>
            </Box>
          </Flex>
        ),
      },
      {
        label: 'common.v-break',
        subLabel: 'in',
        key: '_breakZ',
        sortRowsFn: (a, b, dir) =>
          (a.average.break.zInches > b.average.break.zInches ? 1 : -1) * dir,
        formatFn: (m) => (
          <Flex direction="column" pt="2" pb="2">
            <Box>
              <Text>{m.average.break.zInches.toFixed(1)}</Text>
            </Box>
            <Box>
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                Target: {m.target.break.zInches.toFixed(1)}
              </Text>
            </Box>
            <Box>
              <Badge
                color={
                  Math.abs(m.delta.break.zInches) > MAX_BREAKS_DELTA_INCHES
                    ? RADIX.COLOR.DANGER
                    : RADIX.COLOR.SUCCESS
                }
              >
                {m.delta.break.zInches > 0 && '+'}
                {m.delta.break.zInches.toFixed(1)}
              </Badge>
            </Box>
          </Flex>
        ),
      },
      {
        label: 'common.h-break',
        subLabel: 'in',
        key: '_breakX',
        sortRowsFn: (a, b, dir) =>
          (a.average.break.xInches > b.average.break.xInches ? 1 : -1) * dir,
        formatFn: (m) => (
          <Flex direction="column" pt="2" pb="2">
            <Box>
              <Text>{-m.average.break.xInches.toFixed(1)}</Text>
            </Box>
            <Box>
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                Target: {-m.target.break.xInches.toFixed(1)}
              </Text>
            </Box>
            <Box>
              <Badge
                color={
                  Math.abs(m.delta.break.xInches) > MAX_BREAKS_DELTA_INCHES
                    ? RADIX.COLOR.DANGER
                    : RADIX.COLOR.SUCCESS
                }
              >
                {-m.delta.break.xInches > 0 && '+'}
                {-m.delta.break.xInches.toFixed(1)}
              </Badge>
            </Box>
          </Flex>
        ),
      },
      {
        label: 'common.plate-z',
        subLabel: 'in',
        key: '_plateZ',
        sortRowsFn: (a, b, dir) =>
          (a.average.plate.plate_z > b.average.plate.plate_z ? 1 : -1) * dir,
        formatFn: (m) => (
          <Flex direction="column" pt="2" pb="2">
            <Box>
              <Text>{(m.average.plate.plate_z * FT_TO_INCHES).toFixed(1)}</Text>
            </Box>
            <Box>
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                Target: {(m.target.plate.plate_z * FT_TO_INCHES).toFixed(1)}
              </Text>
            </Box>
            <Box>
              <Badge
                color={
                  Math.abs(m.delta.plate.plate_z * FT_TO_INCHES) >
                  MAX_PLATE_DELTA_IN
                    ? RADIX.COLOR.DANGER
                    : RADIX.COLOR.SUCCESS
                }
              >
                {m.delta.plate.plate_z > 0 && '+'}
                {(m.delta.plate.plate_z * FT_TO_INCHES).toFixed(1)}
              </Badge>
            </Box>
          </Flex>
        ),
      },
      {
        label: 'common.plate-x',
        subLabel: 'in',
        key: '_plateX',
        sortRowsFn: (a, b, dir) =>
          (a.average.plate.plate_x > b.average.plate.plate_x ? 1 : -1) * dir,
        formatFn: (m) => (
          <Flex direction="column" pt="2" pb="2">
            <Box>
              <Text>{(m.average.plate.plate_x * FT_TO_INCHES).toFixed(1)}</Text>
            </Box>
            <Box>
              <Text size="1" color={RADIX.COLOR.SECONDARY}>
                Target: {(m.target.plate.plate_x * FT_TO_INCHES).toFixed(1)}
              </Text>
            </Box>
            <Box>
              <Badge
                color={
                  Math.abs(m.delta.plate.plate_x * FT_TO_INCHES) >
                  MAX_PLATE_DELTA_IN
                    ? RADIX.COLOR.DANGER
                    : RADIX.COLOR.SUCCESS
                }
              >
                {m.delta.plate.plate_x > 0 && '+'}
                {(m.delta.plate.plate_x * FT_TO_INCHES).toFixed(1)}
              </Badge>
            </Box>
          </Flex>
        ),
      },
    ],
    [aggDict]
  );

  // MARK: render
  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonTable
        identifier="PPGroupTable"
        dataTestID={COMPONENT_NAME}
        toolbarContent={
          <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
            <Box>
              <Flex align="center" gap="2">
                <Heading size="4">Pitches</Heading>
                <Badge color={RADIX.COLOR.SECONDARY}>
                  {tableGroups.length}{' '}
                  {t(
                    tableGroups.length === 1 ? 'common.pitch' : 'common.pitches'
                  )}
                </Badge>

                {ONLY_SHOW_CURRENT_MS && (
                  <Badge color={RADIX.COLOR.SUCCESS}>Current MS</Badge>
                )}
              </Flex>
              <Text color={RADIX.COLOR.SECONDARY}>
                Details for each BP pitch type with mean error values
              </Text>
            </Box>
            <Grid columns="3" gap={RADIX.FLEX.GAP.SM}>
              <CommonTextInput
                id="search-groups"
                placeholder="Search Pitch by Name"
                value={groupFilters.search}
                debounceMs={DEFAULT_TEXT_DEBOUNCE_MS}
                onChange={(v) =>
                  usePitchPerformanceStore.setState((prev) => ({
                    groupFilters: {
                      ...prev.groupFilters,
                      search: v ?? '',
                    },
                  }))
                }
                rightSlot={<MagnifyingGlassIcon />}
                disabled={loading}
              />

              <CommonSearchInput
                id="pitch-list-search-type"
                name="type"
                placeholder="common.pitch-type"
                options={PITCH_TYPE_FILTER_OPTIONS}
                values={groupFilters.pitchType ? [groupFilters.pitchType] : []}
                onChange={(v) =>
                  usePitchPerformanceStore.setState((prev) => ({
                    groupFilters: {
                      ...prev.groupFilters,
                      pitchType: first(v) as PitchType,
                    },
                  }))
                }
                disabled={loading}
                optional
                skipSort
              />

              <CommonSelectInput
                id="toggle-checked-groups"
                options={[
                  { label: 'Show All', value: 'false' },
                  { label: 'Show Checked', value: 'true' },
                ]}
                value={groupFilters.onlyShowChecked ? 'true' : 'false'}
                onBooleanChange={(v) =>
                  usePitchPerformanceStore.setState((prev) => ({
                    groupFilters: {
                      ...prev.groupFilters,
                      onlyShowChecked: v,
                    },
                  }))
                }
                disabled={loading}
              />
            </Grid>
          </Flex>
        }
        scrollHeight="400px"
        displayColumns={columns}
        displayData={tableGroups}
        loading={loading}
        {...pagination}
        {...checkable}
        {...sort}
      />
    </ErrorBoundary>
  );
};
